import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { EditorContent, useEditor } from "@tiptap/react";
import { Box, Button, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  buildEditorExtensions,
  chipsToTokens,
  labelForToken,
  MUSTACHE_TAG,
  tokensToChips,
} from "../../utils/mustacheHtml";

const useStyles = makeStyles({
  frame: {
    border: "1px solid #98a2b3",
    borderRadius: "6px",
    backgroundColor: "#fff",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    padding: "4px",
    borderBottom: "1px solid #d0d5dd",
    backgroundColor: "#f8fafc",
  },
  toolButton: {
    minWidth: "44px",
    minHeight: "44px",
    textTransform: "none",
    fontWeight: 600,
  },
  active: { backgroundColor: "#dbe7ff" },
});

/**
 * Rich-text editor for simple HTML email bodies. Mustache tags are atomic chips; the parent
 * always receives plain Mustache HTML through `onChange`.
 */
const VisualEditor = forwardRef(
  ({ value, onChange, variables, onFocus, ariaLabel }, ref) => {
    const classes = useStyles();
    const lastEmitted = useRef(value);

    const extensions = useMemo(
      () => buildEditorExtensions((token) => labelForToken(token, variables)),
      [variables],
    );

    const editor = useEditor(
      {
        extensions,
        content: tokensToChips(value),
        editorProps: {
          attributes: {
            role: "textbox",
            "aria-multiline": "true",
            "aria-label": ariaLabel,
          },
        },
        onUpdate: ({ editor: current }) => {
          const html = chipsToTokens(current.getHTML());
          lastEmitted.current = html;
          onChange(html);
        },
        onFocus: () => onFocus && onFocus(),
      },
      [extensions],
    );

    // Adopt external changes (e.g. switching from source view) without echoing our own.
    useEffect(() => {
      if (!editor || value === lastEmitted.current) return;
      lastEmitted.current = value;
      editor.commands.setContent(tokensToChips(value), false);
    }, [editor, value]);

    useImperativeHandle(
      ref,
      () => ({
        /** Inserts a token; a `{{#x}}{{/x}}` section token becomes an open and a close chip. */
        insertToken: (token) => {
          if (!editor) return;
          MUSTACHE_TAG.lastIndex = 0;
          const parts = token.match(MUSTACHE_TAG) || [token];
          editor
            .chain()
            .focus()
            .insertContent(
              parts.map((part) => ({
                type: "mustacheVariable",
                attrs: { token: part },
              })),
            )
            .run();
        },
        focus: () => editor && editor.commands.focus(),
      }),
      [editor],
    );

    if (!editor) return null;

    const tool = (label, title, isActive, action, disabled = false) => (
      <Tooltip title={title} key={title}>
        <span>
          <Button
            className={`${classes.toolButton} ${
              isActive ? classes.active : ""
            }`}
            aria-label={title}
            aria-pressed={isActive}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={action}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );

    const setLink = () => {
      const previous = editor.getAttributes("link").href || "";
      // eslint-disable-next-line no-alert
      const href = window.prompt(
        "Link address. Use a variable such as {{{resetUrl}}} for links the system builds.",
        previous,
      );
      if (href === null) return;
      if (href.trim() === "") {
        editor.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: href.trim() })
        .run();
    };

    return (
      <Box className={`${classes.frame} mt-visual`}>
        <Box className={classes.toolbar} role="toolbar" aria-label="Formatting">
          {tool("B", "Bold", editor.isActive("bold"), () =>
            editor.chain().focus().toggleBold().run(),
          )}
          {tool("I", "Italic", editor.isActive("italic"), () =>
            editor.chain().focus().toggleItalic().run(),
          )}
          {tool("H2", "Heading", editor.isActive("heading", { level: 2 }), () =>
            editor.chain().focus().toggleHeading({ level: 2 }).run(),
          )}
          {tool("• List", "Bulleted list", editor.isActive("bulletList"), () =>
            editor.chain().focus().toggleBulletList().run(),
          )}
          {tool(
            "1. List",
            "Numbered list",
            editor.isActive("orderedList"),
            () => editor.chain().focus().toggleOrderedList().run(),
          )}
          {tool("Link", "Add or edit link", editor.isActive("link"), setLink)}
          {tool(
            "Undo",
            "Undo",
            false,
            () => editor.chain().focus().undo().run(),
            !editor.can().undo(),
          )}
          {tool(
            "Redo",
            "Redo",
            false,
            () => editor.chain().focus().redo().run(),
            !editor.can().redo(),
          )}
        </Box>
        <EditorContent editor={editor} />
      </Box>
    );
  },
);

VisualEditor.displayName = "VisualEditor";

VisualEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  variables: PropTypes.arrayOf(
    PropTypes.shape({ name: PropTypes.string, label: PropTypes.string }),
  ),
  onFocus: PropTypes.func,
  ariaLabel: PropTypes.string,
};

VisualEditor.defaultProps = {
  value: "",
  variables: [],
  onFocus: undefined,
  ariaLabel: "Message body",
};

export default VisualEditor;
